import { useNeighborhoodContext } from '@/pages/neighborhoods/useNeighborhoodContext';
import { EmptyTableState, Loading, RichTextContent } from '@startupquickstart/frontend';
import { FileText, Globe, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '@startupquickstart/frontend';

export function NeighborhoodOverviewTab() {
  const { neighborhood, isLoading } = useNeighborhoodContext();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!neighborhood) return null;

  return (
    <div className="space-y-6">
      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          {/* About card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">About</h2>
              <p className="text-sm text-muted-foreground">
                Neighborhood description and details
              </p>
            </div>
            <div className="p-6">
              {neighborhood.description ? (
                <RichTextContent content={neighborhood.description} />
              ) : (
                <EmptyTableState
                  icon={<FileText className="h-6 w-6" />}
                  title="No description"
                  description="Edit to add a description"
                />
              )}
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(neighborhood.createdAt)}</span>
              <span>Updated: {formatDate(neighborhood.updatedAt)}</span>
            </div>
          </div>
        </div>

        {/* Right column - Location */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Location</h2>
              <p className="text-sm text-muted-foreground">Geographic details</p>
            </div>
            <div className="p-6">
              <div className="divide-y">
                {neighborhood.greaterArea && (
                  <div className="py-3 first:pt-0">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      Greater Area
                    </div>
                    <div className="text-sm">
                      <Link
                        to={`/greater-areas/${neighborhood.greaterArea.id}`}
                        className="text-primary hover:underline"
                      >
                        {neighborhood.greaterArea.name}
                      </Link>
                    </div>
                  </div>
                )}
                {neighborhood.country && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 shrink-0" />
                      Country
                    </div>
                    <div className="text-sm">{neighborhood.country}</div>
                  </div>
                )}
                {neighborhood.state && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      State
                    </div>
                    <div className="text-sm">{neighborhood.state}</div>
                  </div>
                )}
                {neighborhood.city && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      City
                    </div>
                    <div className="text-sm">{neighborhood.city}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
